/**
 * Diálogo de documentos de una venta al contado (acto de venta, descargo...):
 * se elige una plantilla de tipo "contado" y sale por impresión/PDF o como
 * Word. Mismo flujo que el del listado de motores facturados; lo usa la
 * pestaña Compras del expediente del cliente. El backend acepta el permiso
 * de las pantallas de ventas, del informe o del expediente.
 *
 * venta: { secuencia, nombre, chasis } o null (cerrado).
 */
import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { useGlobalMDAlert } from "context";
import { FP, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { imprimirHtml, descargarB64 } from "modules/documentacion/documentos";

export default function DocumentosVentaContado({ venta, onClose }) {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaSel, setPlantillaSel] = useState("");
  const [cargando, setCargando] = useState(false);
  const [generando, setGenerando] = useState(""); // "" | "imprimir" | "word"

  useEffect(() => {
    if (!venta) return undefined;
    let activo = true;
    setPlantillas([]);
    setPlantillaSel("");
    setCargando(true);
    post("/api/ventacontado_documento_plantillas", { secuencia: venta.secuencia })
      .then(({ ok, data }) => {
        if (!activo) return;
        if (!ok) {
          showAlert({
            message: data?.Error || "No se pudieron cargar las plantillas",
            color: "error",
          });
          onClose();
          return;
        }
        const lista = data.plantillas || [];
        setPlantillas(lista);
        // Con una sola plantilla no tiene sentido obligar a elegirla.
        if (lista.length === 1) setPlantillaSel(lista[0].id);
      })
      .finally(() => {
        if (activo) setCargando(false);
      });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venta]);

  const generar = async (modo) => {
    if (!plantillaSel) return;
    setGenerando(modo);
    try {
      const { ok, data } = await post("/api/ventacontado_documento_generar", {
        id: plantillaSel,
        secuencia: venta.secuencia,
      });
      if (!ok) {
        showAlert({ message: data?.Error || "No se pudo generar el documento", color: "error" });
        return;
      }
      if (modo === "imprimir") {
        imprimirHtml(data.html || "", data.nombre);
      } else if (data.archivo_b64) {
        descargarB64(data.archivo_b64, data.nombre);
      } else {
        showAlert({ message: "No se pudo convertir a Word; use Imprimir / PDF", color: "warning" });
        return;
      }
      onClose();
    } finally {
      setGenerando("");
    }
  };

  return (
    <Dialog
      open={!!venta}
      onClose={() => !generando && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
        Documentos de la venta #{venta?.secuencia}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: FP.color.textSecondary, mb: 1, fontSize: "0.9rem" }}>
          {venta?.nombre}
          {venta?.chasis ? ` — ${venta.chasis}` : ""}
        </Typography>
        {cargando && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!cargando && plantillas.length === 0 && (
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            No hay plantillas de venta al contado. Cree una en Configuración → Documentos, eligiendo
            &quot;Venta al contado&quot; como origen de los datos.
          </DialogContentText>
        )}
        {!cargando && plantillas.length > 0 && (
          <TextField
            select
            fullWidth
            label="Plantilla"
            value={plantillaSel}
            onChange={(e) => setPlantillaSel(e.target.value)}
            sx={{ ...fpInputSx, mt: 1 }}
          >
            {plantillas.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.nombre}
                {p.descripcion ? ` — ${p.descripcion}` : ""}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={!!generando}
          sx={{ textTransform: "none", borderRadius: 1.2 }}
        >
          Cerrar
        </Button>
        <Button
          variant="outlined"
          startIcon={
            generando === "word" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DownloadOutlinedIcon />
            )
          }
          onClick={() => generar("word")}
          disabled={!!generando || !plantillaSel}
          sx={{ textTransform: "none", borderRadius: 1.2 }}
        >
          Word
        </Button>
        <Button
          variant="contained"
          startIcon={
            generando === "imprimir" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PrintOutlinedIcon />
            )
          }
          onClick={() => generar("imprimir")}
          disabled={!!generando || !plantillaSel}
          sx={{ textTransform: "none", borderRadius: 1.2, color: "#fff" }}
        >
          Imprimir / PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}
